import { auth } from '../config/firebase';
import { 
  Persona, 
  PersonasResponse, 
  PersonaResponse, 
  CreatePersonaRequest, 
  UpdatePersonaRequest 
} from '../types/personas';
import { useTranslation } from 'react-i18next';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.socialness.ai';

/**
 * Fetch all personas for the current user
 * @param brandId Optional brand ID to filter personas
 * @param withAnalysis Optional flag to include analysis data
 * @param appLanguage Optional language code for localized content
 * @returns Promise with the personas data
 */
export const fetchPersonas = async (
  brandId?: string,
  withAnalysis: boolean = false,
  appLanguage?: string
): Promise<PersonasResponse> => {
  let url = ''; // Declare url variable at function scope
  
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Start with the base URL
    url = `${API_URL}/management/personas?`;
    
    // Add query parameters
    const params = new URLSearchParams();
    
    if (brandId) {
      params.append('brandId', brandId);
    }
    
    // Always include withAnalysis=true as specified in the example
    params.append('withAnalysis', withAnalysis ? 'true' : 'false');
    
    // Add language parameter if provided, otherwise use browser language or default to 'en'
    if (appLanguage) {
      params.append('appLanguage', appLanguage);
    } else {
      // Get browser language (first 2 chars for language code)
      const browserLang = navigator.language.split('-')[0];
      params.append('appLanguage', browserLang || 'en');
    }
    
    // Append params to URL
    url += params.toString();

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const responseText = await response.text();
    
    if (!response.ok) {
      console.error('API error response:', responseText);
      throw new Error(`Failed to fetch personas: ${response.status} ${response.statusText}`);
    }

    // Parse the response text as JSON
    const responseData = responseText ? JSON.parse(responseText) : {};
    
    // Check if the response has the expected structure
    if (!responseData.personas) {
      console.warn('API response does not contain personas array:', responseData);
      // Return a default structure to prevent errors
      return {
        success: true,
        personas: [],
        totalPersonas: '0',
        averageCompleteness: '0',
        personasPerBrand: {}
      };
    }
    
    // Normalize field names in the response (avatarURL -> avatarUrl)
    if (responseData.personas && Array.isArray(responseData.personas)) {
      responseData.personas = responseData.personas.map((persona: any) => {
        const normalizedPersona = { ...persona } as Record<string, any>;
        
        // Map avatarURL to avatarUrl for frontend compatibility
        if (normalizedPersona.avatarURL && !normalizedPersona.avatarUrl) {
          normalizedPersona.avatarUrl = normalizedPersona.avatarURL;
        }
        
        return normalizedPersona;
      });
    }
    
    return responseData;
  } catch (error: any) {
    console.error('Error fetching personas:', error);
    console.error('Error details:', {
      message: error?.message || 'Unknown error',
      stack: error?.stack,
      url,
      brandId,
      withAnalysis,
      appLanguage
    });
    
    // Instead of returning empty data, throw the error to let the UI handle it
    throw new Error(`Failed to fetch personas: ${error?.message || 'Unknown error'}`);
  }
};

/**
 * Fetch a single persona by ID
 * @param personaId The ID of the persona to fetch
 * @returns Promise with the persona data
 */
export const fetchPersonaById = async (personaId: string): Promise<PersonaResponse> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_URL}/management/personas/${personaId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch persona: ${response.status}`);
    }

    const responseData = await response.json();
    
    // Normalize field names in the response (avatarURL -> avatarUrl)
    if (responseData.persona) {
      if (responseData.persona.avatarURL && !responseData.persona.avatarUrl) {
        responseData.persona.avatarUrl = responseData.persona.avatarURL;
      }
    }
    
    return responseData;
  } catch (error) {
    console.error(`Error fetching persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Create a new persona
 * @param personaData The data for the new persona
 * @returns Promise with the created persona data
 */
export const createPersona = async (personaData: CreatePersonaRequest): Promise<PersonaResponse> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create exact format matching the successful example
    const cleanData = {
      name: personaData.name,
      brandId: personaData.brandId,
      age: typeof personaData.age === 'number' ? personaData.age : 28,
      gender: personaData.gender || 'Other',
      location: personaData.location || '',
      language: Array.isArray(personaData.languages) && personaData.languages.length > 0 ? 
                personaData.languages[0] : 'en',
      jobTitle: personaData.jobTitle || '',
      income: personaData.income || '',
      education: personaData.education || '',
      relationship: personaData.relationship || '',
      avatarURL: personaData.avatarURL || personaData.avatarUrl || '',
      bio: personaData.bio || '',
      tags: Array.isArray(personaData.tags) ? personaData.tags : [],
      ageRange: personaData.ageRange || '25-34',
      languages: Array.isArray(personaData.languages) ? personaData.languages : ['en'],
      
      // Use the exact social network format from example
      socialNetworks: Array.isArray(personaData.socialNetworks) ? 
        personaData.socialNetworks.map(network => ({
          platform: network.platform,
          pertinence: network.pertinence,
          frequency: network.frequency
        })) : [],
      
      formatLength: Array.isArray(personaData.formatLength) ? personaData.formatLength : [],
      
      // Simple string fields - use exactly as provided or empty string
      problems: typeof personaData.problems === 'string' ? personaData.problems : 
                Array.isArray(personaData.problems) ? personaData.problems.join('\n') : '',
      goals: typeof personaData.goals === 'string' ? personaData.goals : 
             Array.isArray(personaData.goals) ? personaData.goals.join('\n') : '',
      motivations: typeof personaData.motivations === 'string' ? personaData.motivations : 
                  Array.isArray(personaData.motivations) ? personaData.motivations.join('\n') : '',
      challenges: typeof personaData.challenges === 'string' ? personaData.challenges : 
                 Array.isArray(personaData.challenges) ? personaData.challenges.join('\n') : '',
      
      // Include these exact fields with the same naming 
      brand_affinity: personaData.brand_affinity || '',
      quote: personaData.quote || '',
      buying_behavior: personaData.buying_behavior || ''
    };
    
    console.log('Creating persona with data:', JSON.stringify(cleanData, null, 2));

    const response = await fetch(`${API_URL}/management/personas`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(cleanData)
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.error(`API error (${response.status}):`, responseText);
      throw new Error(`Failed to create persona: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating persona:', error);
    throw error;
  }
};

/**
 * Update an existing persona
 * @param personaData The data to update the persona with
 * @returns Promise with the updated persona data
 */
export const updatePersona = async (personaData: UpdatePersonaRequest): Promise<PersonaResponse> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const { id, ...updateDataRaw } = personaData;
    
    // Simplify to match the working example
    const updateData: Record<string, any> = {};
    
    // Only include fields that are present in the request
    if (updateDataRaw.name !== undefined) updateData.name = updateDataRaw.name;
    if (updateDataRaw.jobTitle !== undefined) updateData.jobTitle = updateDataRaw.jobTitle;
    if (updateDataRaw.bio !== undefined) updateData.bio = updateDataRaw.bio;
    if (updateDataRaw.tags !== undefined) updateData.tags = updateDataRaw.tags;
    if (updateDataRaw.age !== undefined) updateData.age = updateDataRaw.age;
    if (updateDataRaw.ageRange !== undefined) updateData.ageRange = updateDataRaw.ageRange;
    if (updateDataRaw.location !== undefined) updateData.location = updateDataRaw.location;
    if (updateDataRaw.income !== undefined) updateData.income = updateDataRaw.income;
    if (updateDataRaw.education !== undefined) updateData.education = updateDataRaw.education;
    if (updateDataRaw.relationship !== undefined) updateData.relationship = updateDataRaw.relationship;
    if (updateDataRaw.brandId !== undefined) updateData.brandId = updateDataRaw.brandId;
    if (updateDataRaw.languages !== undefined) updateData.languages = updateDataRaw.languages;
    if (updateDataRaw.formatLength !== undefined) updateData.formatLength = updateDataRaw.formatLength;
    
    // Handle the avatar URL - use capital URL as in the API
    if (updateDataRaw.avatarURL !== undefined) {
      updateData.avatarURL = updateDataRaw.avatarURL;
    } else if (updateDataRaw.avatarUrl !== undefined) {
      updateData.avatarURL = updateDataRaw.avatarUrl;
    }
    
    // Handle social networks - if present
    if (updateDataRaw.socialNetworks !== undefined) {
      updateData.socialNetworks = updateDataRaw.socialNetworks;
    }
    
    // Handle string fields directly without conversion
    if (updateDataRaw.problems !== undefined) {
      updateData.problems = updateDataRaw.problems;
    }
    
    if (updateDataRaw.goals !== undefined) {
      updateData.goals = updateDataRaw.goals;
    }
    
    if (updateDataRaw.motivations !== undefined) {
      updateData.motivations = updateDataRaw.motivations;
    }
    
    if (updateDataRaw.challenges !== undefined) {
      updateData.challenges = updateDataRaw.challenges;
    }
    
    // Additional fields
    if (updateDataRaw.brand_affinity !== undefined) updateData.brand_affinity = updateDataRaw.brand_affinity;
    if (updateDataRaw.quote !== undefined) updateData.quote = updateDataRaw.quote;
    if (updateDataRaw.buying_behavior !== undefined) updateData.buying_behavior = updateDataRaw.buying_behavior;
    
    console.log('Updating persona with ID:', id);
    console.log('Update data:', JSON.stringify(updateData, null, 2));

    const response = await fetch(`${API_URL}/management/personas/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    // Try to get response text first for better debugging
    let responseText = '';
    try {
      responseText = await response.text();
    } catch (error) {
      console.error('Failed to read response text:', error);
    }
    
    if (!response.ok) {
      console.error('API error response:', responseText);
      throw new Error(`Failed to update persona: ${response.status}`);
    }

    // Parse the response text as JSON if possible
    let responseData = {};
    try {
      responseData = responseText ? JSON.parse(responseText) : {};
    } catch (error) {
      console.error('Failed to parse response JSON:', error);
      // Return minimal valid response to prevent errors
      return { 
        success: true,
        persona: { id, ...updateDataRaw } as Persona 
      };
    }
    
    return responseData as PersonaResponse;
  } catch (error) {
    console.error(`Error updating persona:`, error);
    throw error;
  }
};

/**
 * Delete a persona
 * @param personaId The ID of the persona to delete
 * @returns Promise with the deletion result
 */
export const deletePersona = async (personaId: string): Promise<{ success: boolean }> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_URL}/management/personas/${personaId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to delete persona: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error(`Error deleting persona ${personaId}:`, error);
    throw error;
  }
};